import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCommissions, payCommission, payCommissionsBatch, downloadLiquidationPDF, sendLiquidationEmail, cancelCommission, getUsers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { BadgeDollarSign, CheckCircle, Clock, ChevronDown, ChevronUp, Square, CheckSquare, Download, Mail, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const formatMoney = (n) => {
  try { return `RD$${Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
  catch { return 'RD$0.00'; }
};

const formatDate = (val) => {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return val; }
};

const SOURCE_LABELS = { pos: 'Venta POS', tailoring: 'Confección', service: 'Servicio' };
const SOURCE_COLORS = { pos: 'bg-cyan-100 text-cyan-700', tailoring: 'bg-purple-100 text-purple-700', service: 'bg-orange-100 text-orange-700' };

const Commissions = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [items, setItems]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [loading, setLoading]           = useState(true);
  const [payingId, setPayingId]         = useState(null);
  const [payingBatch, setPayingBatch]   = useState(false);
  const [downloadingLiq, setDownloadingLiq] = useState(null);
  const [emailModal, setEmailModal]         = useState(null); // { liqNumber, email }
  const [sendingEmail, setSendingEmail]     = useState(false);
  const [expanded, setExpanded]         = useState({});
  const [sellers, setSellers]           = useState([]);
  const [selected, setSelected]         = useState(new Set());
  const [cancelModal, setCancelModal]   = useState(null); // { id }
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  const [filters, setFilters] = useState({
    status: 'pendiente',
    start_date: '',
    end_date: '',
    seller_id: '',
  });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const data = await getCommissions(active, p, 25);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPendingTotal(data.pending_total || 0);
      setPage(p);
    } catch {
      toast.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isAdmin) getUsers().then(u => setSellers(u || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(1); }, [load]);

  const pendingItems = items.filter(i => i.status === 'pendiente');
  const allPendingIds = pendingItems.map(i => i.id);
  const allSelected = allPendingIds.length > 0 && allPendingIds.every(id => selected.has(id));
  const selectedList = [...selected];
  const selectedSum  = items
    .filter(i => selected.has(i.id))
    .reduce((s, i) => s + (i.total_commission || 0), 0);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allPendingIds));
    }
  };

  const handlePay = async (id) => {
    setPayingId(id);
    try {
      const res = await payCommission(id);
      const liqNum = res.liquidation_number;
      toast.success(`Comisión pagada · ${liqNum}`, {
        action: {
          label: 'Descargar',
          onClick: () => handleDownloadLiq(liqNum),
        },
        duration: 8000,
      });
      openEmailModal(liqNum);
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar pago');
    } finally {
      setPayingId(null);
    }
  };

  const handleDownloadLiq = async (liqNumber) => {
    setDownloadingLiq(liqNumber);
    try {
      const blob = await downloadLiquidationPDF(liqNumber);
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `liquidacion_${liqNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch {
      toast.error('Error al descargar el comprobante');
    } finally {
      setDownloadingLiq(null);
    }
  };

  const openEmailModal = (liqNumber, sellerEmail = '') => {
    setEmailModal({ liqNumber, email: sellerEmail });
  };

  const handleSendEmail = async () => {
    if (!emailModal?.email?.trim()) return toast.error('Ingresa un correo');
    setSendingEmail(true);
    try {
      await sendLiquidationEmail(emailModal.liqNumber, emailModal.email.trim());
      toast.success(`Comprobante enviado a ${emailModal.email}`);
      setEmailModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePayBatch = async () => {
    if (selectedList.length === 0) return;
    setPayingBatch(true);
    try {
      const res = await payCommissionsBatch(selectedList);
      const liqNum = res.liquidation_number;
      toast.success(`${res.paid_count} comisión(es) pagadas · ${liqNum} · ${formatMoney(res.total_paid)}`, {
        action: {
          label: 'Descargar',
          onClick: () => handleDownloadLiq(liqNum),
        },
        duration: 10000,
      });
      openEmailModal(liqNum, sellers.find(s => s.name === res.seller_name)?.email || '');
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar liquidación');
    } finally {
      setPayingBatch(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancellingId(cancelModal.id);
    try {
      await cancelCommission(cancelModal.id, cancelReason.trim());
      toast.success('Comisión cancelada');
      setCancelModal(null);
      setCancelReason('');
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    } finally {
      setCancellingId(null);
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const pendingSum = pendingItems.reduce((s, i) => s + (i.total_commission || 0), 0);

  return (
    <div className="p-6 md:p-8 space-y-6 pb-32">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Comisiones</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Total pendiente (período)</p>
            <p className="text-2xl font-bold text-amber-600">{formatMoney(pendingTotal)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Registros mostrados</p>
            <p className="text-2xl font-bold text-slate-800">{total}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Pendiente en esta página</p>
            <p className="text-2xl font-bold text-cyan-600">{formatMoney(pendingSum)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Desde</label>
              <Input type="date" value={filters.start_date}
                onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
                className="h-8 text-sm w-36" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Hasta</label>
              <Input type="date" value={filters.end_date}
                onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
                className="h-8 text-sm w-36" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Estado</label>
              <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-sm w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="pagada">Pagada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && sellers.length > 0 && (
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Vendedor</label>
                <Select value={filters.seller_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, seller_id: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-sm w-36"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {sellers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={() => load(1)} className="h-8 bg-cyan-500 hover:bg-cyan-600 text-white text-sm">
              Filtrar
            </Button>
            <Button variant="outline" className="h-8 text-sm" onClick={() => {
              setFilters({ status: 'pendiente', start_date: '', end_date: '', seller_id: '' });
            }}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seleccionar todo (solo admin, solo si hay pendientes en la lista) */}
      {isAdmin && pendingItems.length > 0 && (
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            {allSelected
              ? <CheckSquare className="w-4 h-4 text-cyan-500" />
              : <Square className="w-4 h-4" />}
            {allSelected ? 'Deseleccionar todo' : `Seleccionar ${pendingItems.length} pendiente${pendingItems.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <BadgeDollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
          No hay comisiones registradas
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(com => {
            const isPending    = com.status === 'pendiente';
            const isCancelled = com.status === 'cancelada';
            const isChecked   = selected.has(com.id);
            return (
              <Card
                key={com.id}
                className={`bg-white border shadow-sm transition-colors ${isAdmin && isPending && isChecked ? 'border-cyan-400 ring-1 ring-cyan-300' : 'border-slate-200'}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    {isAdmin && isPending && (
                      <button
                        onClick={() => toggleSelect(com.id)}
                        className="mt-0.5 shrink-0 text-slate-400 hover:text-cyan-500 transition-colors"
                      >
                        {isChecked
                          ? <CheckSquare className="w-5 h-5 text-cyan-500" />
                          : <Square className="w-5 h-5" />}
                      </button>
                    )}

                    <div className="flex-1 flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SOURCE_COLORS[com.source_type] || 'bg-slate-100 text-slate-600'}`}>
                            {SOURCE_LABELS[com.source_type] || com.source_type}
                          </span>
                          <span className="font-medium text-sm text-slate-800">{com.source_number}</span>
                          {com.customer_name && (
                            <span className="text-xs text-slate-500 capitalize">{com.customer_name}</span>
                          )}
                        </div>
                        {isAdmin && (
                          <p className="text-xs text-slate-500">
                            Vendedor: <span className="font-medium text-slate-700">{com.seller_name}</span>
                          </p>
                        )}
                        <p className="text-xs text-slate-400">{formatDate(com.earned_at)}</p>
                        {com.liquidation_number && (
                          <p className="text-xs text-slate-400">Liquidación: <span className="font-medium">{com.liquidation_number}</span></p>
                        )}
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="text-xl font-bold text-emerald-600">{formatMoney(com.total_commission)}</p>
                          {isPending ? (
                            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                              <Clock className="w-3 h-3" /> Pendiente
                            </span>
                          ) : isCancelled ? (
                            <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                              <XCircle className="w-3 h-3" /> Cancelada
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <CheckCircle className="w-3 h-3" /> Pagada
                            </span>
                          )}
                        </div>
                        {isAdmin && isPending && (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              onClick={() => handlePay(com.id)}
                              disabled={payingId === com.id || payingBatch}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white h-8"
                            >
                              {payingId === com.id ? 'Procesando...' : 'Pagar'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setCancelModal({ id: com.id }); setCancelReason(''); }}
                              disabled={payingId === com.id || payingBatch}
                              className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50"
                            >
                              Cancelar
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {com.lines?.length > 0 && (
                  <CardContent className="pt-0 pl-10">
                    <button
                      onClick={() => toggleExpand(com.id)}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {expanded[com.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {expanded[com.id] ? 'Ocultar detalle' : `Ver detalle (${com.lines.length} línea${com.lines.length !== 1 ? 's' : ''})`}
                    </button>

                    {expanded[com.id] && (
                      <div className="mt-3 border border-slate-100 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Producto</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Categoría</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Cant.</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Base</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Regla</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Comisión</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {com.lines.map((line, i) => (
                              <tr key={i}>
                                <td className="px-3 py-2 text-slate-700">{line.product_name}</td>
                                <td className="px-3 py-2 text-slate-500">{line.category || '—'}</td>
                                <td className="px-3 py-2 text-right">{line.quantity}</td>
                                <td className="px-3 py-2 text-right">{formatMoney(line.subtotal)}</td>
                                <td className="px-3 py-2 text-right text-slate-500">
                                  {line.commission_type === 'fixed'
                                    ? `RD$${line.commission_value}/u`
                                    : `${line.commission_value}%`}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-emerald-600">
                                  {formatMoney(line.commission_amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {com.status === 'cancelada' && (
                      <div className="mt-2">
                        <p className="text-xs text-red-400">
                          Cancelada el {formatDate(com.cancelled_at)} por {com.cancelled_by}
                          {com.cancel_reason ? ` · ${com.cancel_reason}` : ''}
                        </p>
                      </div>
                    )}
                    {com.status === 'pagada' && com.paid_at && (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-slate-400">
                          Pagada el {formatDate(com.paid_at)} por {com.paid_by}
                        </p>
                        {com.liquidation_number && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadLiq(com.liquidation_number)}
                              disabled={downloadingLiq === com.liquidation_number}
                              className="flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-800 font-medium transition-colors disabled:opacity-50"
                              title="Descargar comprobante"
                            >
                              <Download className="w-3 h-3" />
                              {downloadingLiq === com.liquidation_number ? 'Descargando...' : com.liquidation_number}
                            </button>
                            <button
                              onClick={() => {
                                const seller = sellers.find(s => s.id === com.seller_id);
                                openEmailModal(com.liquidation_number, seller?.email || '');
                              }}
                              className="flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-600 transition-colors"
                              title="Enviar comprobante por correo"
                            >
                              <Mail className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load(page - 1)} disabled={page <= 1 || loading}>
            Anterior
          </Button>
          <span className="text-sm text-slate-500">Página {page} de {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => load(page + 1)} disabled={page >= totalPages || loading}>
            Siguiente
          </Button>
        </div>
      )}

      {/* Modal envío de comprobante por correo */}
      <Dialog open={!!emailModal} onOpenChange={v => { if (!v) setEmailModal(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enviar comprobante por correo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">
              Liquidación: <span className="font-semibold text-slate-700">{emailModal?.liqNumber}</span>
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Correo del vendedor</Label>
              <Input
                type="email"
                value={emailModal?.email || ''}
                onChange={e => setEmailModal(m => ({ ...m, email: e.target.value }))}
                placeholder="vendedor@ejemplo.com"
                className="h-9"
                onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(null)}>Cancelar</Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailModal?.email?.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              {sendingEmail ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal cancelar comisión */}
      <Dialog open={!!cancelModal} onOpenChange={v => { if (!v) { setCancelModal(null); setCancelReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancelar comisión</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Esta acción no se puede revertir. La comisión quedará marcada como cancelada y no podrá pagarse.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Motivo (opcional)</Label>
              <Input
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Ej: error de cálculo, devolución..."
                className="h-9"
                onKeyDown={e => e.key === 'Enter' && handleCancel()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelModal(null); setCancelReason(''); }}>
              Volver
            </Button>
            <Button
              onClick={handleCancel}
              disabled={cancellingId === cancelModal?.id}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {cancellingId === cancelModal?.id ? 'Cancelando...' : 'Confirmar cancelación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banner sticky de pago múltiple */}
      {isAdmin && selectedList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white shadow-2xl">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelected(new Set())}
                className="text-slate-400 hover:text-white transition-colors text-xs underline"
              >
                Cancelar selección
              </button>
              <span className="text-slate-300 text-sm">
                <span className="font-bold text-white">{selectedList.length}</span> comisión{selectedList.length !== 1 ? 'es' : ''} seleccionada{selectedList.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-lg font-bold text-emerald-400">{formatMoney(selectedSum)}</span>
              <Button
                onClick={handlePayBatch}
                disabled={payingBatch}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6"
              >
                {payingBatch ? 'Procesando...' : 'Pagar seleccionadas'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commissions;
